import { makeNoise2D, makeFbm, clamp, smoothstep, mix } from '../core/rng.js';

// ---------------------------------------------------------------------------
// The polder.
//
// Zaanse Schans is not an island of hills — it is reclaimed marsh: dead flat,
// a metre above the water, stitched together by dikes and drainage ditches
// with the river Zaan running through the middle. So the terrain generator
// spends its effort on waterways and field patterns, not on mountains.
// ---------------------------------------------------------------------------

const CANAL = {
    p0: { x: 0.02, z: 0.10 },
    p1: { x: 0.52, z: 0.40 },
    p2: { x: 0.98, z: 0.94 },
    halfWidth: 5.0,
};

function bezier(t) {
    const s = 1 - t;
    return {
        x: s * s * CANAL.p0.x + 2 * s * t * CANAL.p1.x + t * t * CANAL.p2.x,
        z: s * s * CANAL.p0.z + 2 * s * t * CANAL.p1.z + t * t * CANAL.p2.z,
    };
}

function bezierTangent(t) {
    const s = 1 - t;
    const x = 2 * s * (CANAL.p1.x - CANAL.p0.x) + 2 * t * (CANAL.p2.x - CANAL.p1.x);
    const z = 2 * s * (CANAL.p1.z - CANAL.p0.z) + 2 * t * (CANAL.p2.z - CANAL.p1.z);
    const len = Math.hypot(x, z) || 1;
    return { x: x / len, z: z / len };
}

export function registerTerrainBlocks(palette) {
    return {
        grassA: palette.add('grassA', 0x6f9a4e, { jitter: 0.07 }),
        grassB: palette.add('grassB', 0x7fa958, { jitter: 0.07 }),
        grassC: palette.add('grassC', 0x628c46, { jitter: 0.07 }),
        grassDry: palette.add('grassDry', 0x93a95c, { jitter: 0.07 }),
        dirt: palette.add('dirt', 0x6b5942, { jitter: 0.06 }),
        clay: palette.add('clay', 0x7d6a4e, { jitter: 0.05 }),
        mud: palette.add('mud', 0x4d4433, { jitter: 0.05 }),
        sand: palette.add('sand', 0xc9b98c, { jitter: 0.06 }),
        path: palette.add('path', 0xbfae8e, { jitter: 0.08 }),
        quay: palette.add('quay', 0x6d6257, { jitter: 0.07 }),
        brick: palette.add('brick', 0x8a4f3d, { jitter: 0.08 }),
    };
}

/**
 * Generate the height field and stamp it into the voxel grid.
 * Returns a `Polder` with the queries the rest of the world needs.
 */
export function buildTerrain(grid, palette, {
    size, seaLevel, seed, voxelSize = 0.4, origin = { x: 0, z: 0 }, canal = true,
}) {
    const B = registerTerrainBlocks(palette);

    const noise = makeNoise2D(seed);
    const fbm = makeFbm(noise, { octaves: 4 });
    const fieldNoise = makeNoise2D(seed + 977);

    const heights = new Int16Array(size * size);   // count of solid voxels
    const surface = new Uint8Array(size * size);   // block id on top
    const canalDist = new Float32Array(size * size);
    const canalSide = new Int8Array(size * size);
    const canalT = new Float32Array(size * size);

    // Sample the canal centreline once; everything else measures against it.
    // Only the Zaanse Schans has a river through it; the fishing villages are
    // just land in the water, so their canal distance stays effectively
    // infinite and every test against it falls away.
    if (!canal) canalDist.fill(1e6);
    const SAMPLES = 260;
    const curve = [];
    const at = (x, z) => z * size + x;

    for (let i = 0; i < SAMPLES; i++) {
        const t = i / (SAMPLES - 1);
        const p = bezier(t);
        curve.push({ t, x: p.x * size, z: p.z * size, tan: bezierTangent(t) });
    }

    // --- pass 1: distance to the canal --------------------------------------
    if (canal) for (let z = 0; z < size; z++) {
        for (let x = 0; x < size; x++) {
            let best = Infinity;
            let bestI = 0;
            for (let i = 0; i < SAMPLES; i++) {
                const dx = x - curve[i].x;
                const dz = z - curve[i].z;
                const d = dx * dx + dz * dz;
                if (d < best) {
                    best = d;
                    bestI = i;
                }
            }
            const s = curve[bestI];
            const dx = x - s.x;
            const dz = z - s.z;
            // Sign via 2D cross product with the tangent: left bank vs right.
            const cross = s.tan.x * dz - s.tan.z * dx;
            const i = at(x, z);
            canalDist[i] = Math.sqrt(best);
            canalSide[i] = cross < 0 ? -1 : 1;
            canalT[i] = s.t;
        }
    }

    // --- pass 2: heights -----------------------------------------------------
    for (let z = 0; z < size; z++) {
        for (let x = 0; x < size; x++) {
            const i = at(x, z);
            const nx = x / size;
            const nz = z / size;

            // Ragged coastline: an ellipse pushed around by low-frequency noise.
            const ex = (nx - 0.5) / 0.455;
            const ez = (nz - 0.5) / 0.435;
            let r = Math.hypot(ex, ez) + fbm(nx * 2.6, nz * 2.6) * 0.30;

            // Flat land, one voxel of gentle relief. This is the Netherlands.
            let h = seaLevel + 2 + fbm(nx * 7.5 + 40, nz * 7.5) * 1.5;

            // Dike ring just inside the coast, holding the sea out.
            const dike = smoothstep(0.70, 0.84, r) * (1 - smoothstep(0.84, 0.95, r));
            h += dike * 2.4;

            // Beyond the coastline the ground drops away under the water.
            const off = smoothstep(0.90, 1.06, r);
            h = mix(h, seaLevel - 4, off);


            // The Zaan itself, with quay banks either side.
            const cd = canalDist[i];
            if (cd < CANAL.halfWidth + 3.5) {
                const channel = 1 - smoothstep(CANAL.halfWidth - 1.5, CANAL.halfWidth + 1.2, cd);
                h = mix(h, seaLevel - 3.2, channel);
                const bank = smoothstep(CANAL.halfWidth + 0.5, CANAL.halfWidth + 2.0, cd)
                    * (1 - smoothstep(CANAL.halfWidth + 2.0, CANAL.halfWidth + 3.5, cd));
                h += bank * 1.6;
            }

            // Drainage ditches: thin parallel water strips slicing the meadows
            // into the patchwork strips every Dutch polder is made of.
            //
            // Only where there is a canal to drain into. The test is distance
            // from that canal, and an island without one has infinite distance
            // everywhere — so every cell qualified and the fishing villages
            // were being sliced up by ditches that drained into nothing.
            if (canal && cd > CANAL.halfWidth + 5 && r < 0.82) {
                const along = (x * 0.42 + z * 0.28) + fbm(nx * 3, nz * 3) * 2.2;
                const ditch = Math.abs((along % 9) - 4.5);
                if (ditch < 0.65) h = mix(h, seaLevel - 0.6, 1 - ditch / 0.65);
            }

            // --- open water at the edges -------------------------------
            // Applied last, after the canal and the ditches, because the Zaan
            // runs off both corners of the map and would otherwise carve its
            // bed back up out of the deep.
            //
            // A seabed that simply stops at the grid boundary reads as a slab
            // floating in the sea — a straight rectangular edge, plainly
            // visible through the water. Falling away to nothing before the
            // boundary means the outermost columns are already empty, and the
            // water shader (which treats anything off-map as very deep) then
            // carries the same deep colour outward with no seam to notice.
            const abyss = smoothstep(1.00, 1.18, r);
            h = mix(h, 0, abyss);

            // The ellipse comes closer to the boundary at the edge midpoints
            // than at the corners, so clamp the outermost ring to nothing
            // regardless of where the coastline noise happened to put it.
            const border = Math.min(x, z, size - 1 - x, size - 1 - z);
            h = mix(h, 0, 1 - smoothstep(4, 13, border));

            heights[i] = Math.max(0, Math.round(h));
        }
    }

    // --- pass 3: materials + voxels -----------------------------------------
    for (let z = 0; z < size; z++) {
        for (let x = 0; x < size; x++) {
            const i = at(x, z);
            const top = heights[i];
            if (top <= 0) continue;

            const nx = x / size;
            const nz = z / size;
            const cd = canalDist[i];
            const surfaceY = top - 1;

            // Which green? Low-frequency noise gives each field its own tint,
            // so the meadows read as separate parcels of land.
            const field = fieldNoise(nx * 5.5, nz * 5.5);
            let topId;
            if (surfaceY < seaLevel) {
                topId = surfaceY < seaLevel - 2 ? B.mud : B.sand;
            } else if (cd < CANAL.halfWidth + 3.0) {
                topId = B.quay;                       // stone edge along the water
            } else if (field > 0.22) {
                topId = B.grassB;
            } else if (field < -0.28) {
                topId = B.grassC;
            } else if (field > 0.05 && field < 0.12) {
                topId = B.grassDry;
            } else {
                topId = B.grassA;
            }

            surface[i] = topId;

            for (let y = 0; y < top; y++) {
                let id;
                if (y === top - 1) id = topId;
                else if (y >= top - 3) id = surfaceY < seaLevel ? B.mud : B.dirt;
                else id = B.clay;
                grid.set(x, y, z, id);
            }
        }
    }

    const span = size * voxelSize;

    const polder = {
        size,
        seaLevel,
        voxelSize,
        /** Where the middle of this island sits in the world. */
        origin,
        span,

        // --- the one place grid coordinates become world ones ---------------
        // Every island has its own patch of grid and its own place in the sea,
        // so nothing outside this file should be doing the arithmetic itself.
        toWorld(gx, gz) {
            return {
                x: origin.x + gx * voxelSize - span / 2,
                z: origin.z + gz * voxelSize - span / 2,
            };
        },
        toGrid(wx, wz) {
            return {
                gx: Math.round((wx - origin.x + span / 2) / voxelSize),
                gz: Math.round((wz - origin.z + span / 2) / voxelSize),
            };
        },
        /** Is this world point over this island's patch of grid at all? */
        covers(wx, wz) {
            const { gx, gz } = this.toGrid(wx, wz);
            return gx >= 0 && gz >= 0 && gx < size && gz < size;
        },
        /** Height at a world point, or -1 where this island has no say. */
        topAtWorld(wx, wz) {
            const { gx, gz } = this.toGrid(wx, wz);
            return this.top(gx, gz);
        },

        blocks: B,
        heights,
        surface,
        canalDist,
        canalSide,
        canalT,

        /** Top solid voxel index at a cell, or -1 if the column is empty. */
        top(x, z) {
            if (x < 0 || z < 0 || x >= size || z >= size) return -1;
            return heights[at(x, z)] - 1;
        },
        surfaceId(x, z) {
            if (x < 0 || z < 0 || x >= size || z >= size) return 0;
            return surface[at(x, z)];
        },
        distToCanal(x, z) {
            if (x < 0 || z < 0 || x >= size || z >= size) return Infinity;
            return canalDist[at(x, z)];
        },
        isDryLand(x, z) {
            return polder.top(x, z) >= seaLevel;
        },

        /** Grid cell on the canal centreline at parameter t (0..1). */
        canalPoint(t) {
            const p = bezier(clamp(t, 0, 1));
            return { x: p.x * size, z: p.z * size };
        },
        canalTangent(t) {
            return bezierTangent(clamp(t, 0, 1));
        },
        /**
         * A spot on the bank: `t` along the river, `offset` voxels to the side
         * (negative = left bank, where the mills stand).
         */
        bankSpot(t, offset) {
            const p = polder.canalPoint(t);
            const tan = polder.canalTangent(t);
            return {
                x: Math.round(p.x + -tan.z * offset),
                z: Math.round(p.z + tan.x * offset),
            };
        },

        /** Flatten a disc of land, e.g. to seat a windmill or a barn. */
        flatten(cx, cz, radius, level) {
            for (let z = cz - radius; z <= cz + radius; z++) {
                for (let x = cx - radius; x <= cx + radius; x++) {
                    if (x < 0 || z < 0 || x >= size || z >= size) continue;
                    const d = Math.hypot(x - cx, z - cz);
                    if (d > radius) continue;
                    const i = at(x, z);
                    const cur = heights[i];
                    const target = Math.round(mix(level, cur, smoothstep(radius - 2.5, radius, d)));
                    if (target > cur) {
                        for (let y = cur; y < target; y++) {
                            grid.set(x, y, z, y === target - 1 ? surface[i] || B.grassA : B.dirt);
                        }
                    } else if (target < cur) {
                        for (let y = target; y < cur; y++) grid.set(x, y, z, 0);
                        grid.set(x, target - 1, z, surface[i] || B.grassA);
                    }
                    heights[i] = target;
                }
            }
        },

        /** Paint the surface of a cell without changing its height. */
        paint(x, z, id) {
            const y = polder.top(x, z);
            if (y < 0) return;
            grid.set(x, y, z, id);
            surface[at(x, z)] = id;
        },
    };

    return polder;
}

export { CANAL };
